import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Chip,
    TextField,
    InputAdornment,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions
} from '@mui/material';
import { CheckCircle, Printer, Search, Filter, SortAsc, SortDesc, AlertTriangle, Send } from 'lucide-react';
import api from '../utils/api';
import amazonService from '../services/amazonService';

const AchatsEnAttenteExpedition = ({
    onUpdate,
    getStatutLabel,
    getProduitLabel,
    getFournisseurLabel,
    formatDate,
    formatCurrency,
    title = "Achats en attente d'expédition",
    showTitle = true,
    customStyles = {},
    achatsUpdateTrigger
}) => {
    const [loading, setLoading] = useState(false);
    const [achats, setAchats] = useState([]);
    const [statuts, setStatuts] = useState([]);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'dateAchat',
        direction: 'desc'
    });
    const [showSearch, setShowSearch] = useState(false);
    const [dateLivraison, setDateLivraison] = useState('');
    const [pdfUrl, setPdfUrl] = useState(null);
    const [openPdfDialog, setOpenPdfDialog] = useState(false);
    const [confirmAllExpedieOpen, setConfirmAllExpedieOpen] = useState(false);
    const [confirmExpedieId, setConfirmExpedieId] = useState(null);

    // Charger les statuts
    const fetchStatuts = async () => {
        try {
            const response = await api.get('/api/statut');
            let statutsData = response.data;
            if (!Array.isArray(statutsData) && statutsData && Array.isArray(statutsData.data)) {
                statutsData = statutsData.data;
            }
            setStatuts(Array.isArray(statutsData) ? statutsData : []);
        } catch (error) {
            setNotification({
                open: true,
                message: "Erreur lors du chargement des statuts",
                severity: "error"
            });
        }
    };

    // Charger les achats en attente d'expédition
    const fetchAchatsEnAttente = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/achats/en-expedition');
            setAchats(response.data);
        } catch (error) {
            setNotification({
                open: true,
                message: "Erreur lors du chargement des achats en attente d'expédition",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatuts();
        fetchAchatsEnAttente();
    }, [achatsUpdateTrigger]);

    // Fonction pour obtenir les achats filtrés et triés
    const filteredAndSortedAchats = useMemo(() => {
        if (!Array.isArray(achats)) return [];
        let filtered = [...achats];
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(achat =>
                (getProduitLabel(achat.Produit) || '').toLowerCase().includes(searchLower) ||
                (getFournisseurLabel(achat.Fournisseur) || '').toLowerCase().includes(searchLower) ||
                (achat.SKUID || '').toLowerCase().includes(searchLower)
            );
        }
        if (dateLivraison) {
            filtered = filtered.filter(achat => {
                const achatDate = new Date(achat.dateLivraison);
                const filterDate = new Date(dateLivraison);
                return achatDate.toDateString() === filterDate.toDateString();
            });
        }
        return filtered.sort((a, b) => {
            if (sortConfig.key === 'dateAchat') {
                const dateA = new Date(a.dateAchat || 0);
                const dateB = new Date(b.dateAchat || 0);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            if (sortConfig.key === 'prixAchat') {
                const prixA = Number(a.prixAchat) || 0;
                const prixB = Number(b.prixAchat) || 0;
                return sortConfig.direction === 'asc' ? prixA - prixB : prixB - prixA;
            }
            if (sortConfig.key === 'Quantité') {
                const quantiteA = Number(a.Quantité) || 0;
                const quantiteB = Number(b.Quantité) || 0;
                return sortConfig.direction === 'asc' ? quantiteA - quantiteB : quantiteB - quantiteA;
            }
            return 0;
        });
    }, [achats, searchTerm, dateLivraison, sortConfig, getProduitLabel, getFournisseurLabel]);

    // Action pour marquer tous les achats comme expédiés
    const handleMarkAllAsExpedie = async () => {
        setConfirmAllExpedieOpen(false);
        if (filteredAndSortedAchats.length === 0) return;
        setLoading(true);
        try {
            for (const achat of filteredAndSortedAchats) {
                try {
                    if (!statuts || statuts.length === 0) throw new Error("Les statuts ne sont pas encore chargés");
                    const statutExpedie = statuts.find(s => s.Titre === "Expédié");
                    if (!statutExpedie) throw new Error("Statut 'Expédié' non trouvé");
                    await api.put(`/api/achats/${achat.id}`, { Statut: statutExpedie.id });
                } catch (error) {
                    console.error(`Erreur lors du passage en expédié pour l'achat ${achat.id}:`, error);
                }
            }
            if (onUpdate) await onUpdate();
            setNotification({ open: true, message: "Tous les achats ont été marqués comme expédiés", severity: "success" });
            fetchAchatsEnAttente();
        } catch (error) {
            setNotification({ open: true, message: error.message || "Erreur lors du passage en expédié", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Action bouton "Expédié" individuel avec confirmation
    const handleUpdateStatut = async (achatId) => {
        setConfirmExpedieId(achatId);
    };
    const confirmUpdateStatut = async () => {
        if (!confirmExpedieId) return;
        try {
            setLoading(true);
            if (!statuts || statuts.length === 0) throw new Error("Les statuts ne sont pas encore chargés");
            const statutExpedie = statuts.find(s => s.Titre === "Expédié");
            if (!statutExpedie) throw new Error("Statut 'Expédié' non trouvé");
            await api.put(`/api/achats/${confirmExpedieId}`, { Statut: statutExpedie.id });
            if (onUpdate) await onUpdate();
            setNotification({ open: true, message: "Statut mis à jour avec succès", severity: "success" });
            fetchAchatsEnAttente();
        } catch (error) {
            setNotification({ open: true, message: error.message || "Erreur lors de la mise à jour du statut", severity: "error" });
        } finally {
            setLoading(false);
            setConfirmExpedieId(null);
        }
    };

    // Action bouton "Étiquettes"
    const handleEtiquettes = async (achat) => {
        if (!achat.SKUID) {
            setNotification({ open: true, message: "SKU Amazon manquant pour cet achat", severity: "error" });
            return;
        }
        if (!achat.Quantité) {
            setNotification({ open: true, message: "Quantité manquante pour cet achat", severity: "error" });
            return;
        }
        const marketplaceId = 'A13V1IB3VIYZZH'; // France par défaut
        console.log('Génération étiquette avec :', {
            sku: achat.SKUID,
            quantity: achat.Quantité,
            marketplaceId
        });
        setNotification({ open: true, message: "Génération de l'étiquette Amazon...", severity: "info" });
        try {
            const response = await api.get('/api/amazon/item-labels', {
                params: {
                    sku: achat.SKUID,
                    quantity: achat.Quantité,
                    marketplaceId
                }
            });
            
            if (response.data && response.data.documentDownloads && response.data.documentDownloads[0] && response.data.documentDownloads[0].uri) {
                window.open(response.data.documentDownloads[0].uri, '_blank');
                setNotification({ open: true, message: "Étiquette générée avec succès", severity: "success" });
            } else {
                throw new Error("URL de l'étiquette non trouvée dans la réponse");
            }
        } catch (error) {
            setNotification({ open: true, message: error.message || "Erreur lors de la génération de l'étiquette", severity: "error" });
        }
    };

    // Gestion du tri
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />;
    };

    const columns = [
        {
            field: 'Produit',
            headerName: 'Produit',
            valueFormatter: (params) => getProduitLabel(params.value),
            renderCell: (params) => (
                <Tooltip title={getProduitLabel(params.value)}>
                    <span style={{
                        maxWidth: 200,
                        display: 'inline-block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.92rem'
                    }}>{getProduitLabel(params.value)}</span>
                </Tooltip>
            ),
            maxWidth: 160
        },
        {
            field: 'Fournisseur',
            headerName: 'Fournisseur',
            valueFormatter: (params) => getFournisseurLabel(params.value),
            renderCell: (params) => (
                <Tooltip title={getFournisseurLabel(params.value)}>
                    <span style={{
                        maxWidth: 80,
                        display: 'inline-block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.92rem'
                    }}>{getFournisseurLabel(params.value)}</span>
                </Tooltip>
            ),
            maxWidth: 160
        },
        {
            field: 'Quantité',
            headerName: 'Qté',
            width: 50,
            valueFormatter: (params) => params.value,
            renderCell: (params) => <span style={{ fontSize: '0.92rem' }}>{params.value}</span>,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 90,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title="Générer les étiquettes">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEtiquettes(params.row)}
                            sx={{ p: 0.5 }}
                        >
                            <Printer size={16} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Marquer comme expédié">
                        <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUpdateStatut(params.row.id)}
                            sx={{ p: 0.5 }}
                        >
                            <CheckCircle size={16} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // Action pour générer toutes les étiquettes (un seul appel API avec tous les achats)
    const handleGenerateAllLabels = async () => {
        const marketplaceId = 'A13V1IB3VIYZZH'; // France par défaut
        const achatsWithSku = filteredAndSortedAchats.filter(achat => achat.SKUID && achat.Quantité);
        if (achatsWithSku.length === 0) {
            setNotification({ open: true, message: "Aucun achat avec SKU et quantité trouvé", severity: "warning" });
            return;
        }
        setNotification({ open: true, message: "Génération des étiquettes Amazon...", severity: "info" });
        try {
            const mskuQuantities = achatsWithSku.map(achat => ({ msku: achat.SKUID, quantity: achat.Quantité }));
            const response = await api.post('/api/amazon/item-labels', {
                mskuQuantities,
                marketplaceId
            });
            if (response.data && response.data.documentDownloads && response.data.documentDownloads[0] && response.data.documentDownloads[0].uri) {
                window.open(response.data.documentDownloads[0].uri, '_blank');
                setNotification({ open: true, message: "Étiquettes générées avec succès", severity: "success" });
            } else {
                throw new Error("URL de l'étiquette non trouvée dans la réponse");
            }
        } catch (error) {
            setNotification({ open: true, message: error.message || "Erreur lors de la génération des étiquettes", severity: "error" });
        }
    };

    return (
        <Box sx={{ mt: 4, mb: 4, ...customStyles }}>
            {showTitle && (
                <Typography variant="h5" gutterBottom>
                    {title}
                </Typography>
            )}
            <Paper sx={{ p: 1.5, width: '100%', boxShadow: 1, fontSize: '0.92rem', minWidth: 0, overflowX: 'auto' }}>
                {/* Icône imprimante pour générer toutes les étiquettes */}
                <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Tooltip title="Générer toutes les étiquettes">
                        <IconButton
                            onClick={handleGenerateAllLabels}
                            size="small"
                            color="primary"
                            disabled={loading || filteredAndSortedAchats.length === 0}
                        >
                            <Printer size={20} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Tout marquer comme expédié">
                        <IconButton
                            onClick={() => setConfirmAllExpedieOpen(true)}
                            size="small"
                            color="success"
                            disabled={loading || filteredAndSortedAchats.length === 0}
                        >
                            <Send size={20} />
                        </IconButton>
                    </Tooltip>
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredAndSortedAchats.length === 0 ? (
                    <Typography variant="body1" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        {searchTerm ? "Aucun résultat trouvé" : "Aucun achat en attente d'expédition"}
                    </Typography>
                ) : (
                    <TableContainer sx={{ width: '100%', minWidth: 0 }}>
                        <Table size="small" sx={{ width: '100%', minWidth: 0 }}>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.field}
                                            sortDirection={sortConfig.key === column.field ? sortConfig.direction : false}
                                            sx={{
                                                fontSize: '0.92rem',
                                                px: 1,
                                                py: 0.5,
                                                whiteSpace: 'nowrap',
                                                maxWidth: column.maxWidth,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minWidth: 60,
                                                fontWeight: 500,
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                                                 onClick={() => handleSort(column.field)}>
                                                {column.headerName}
                                                {getSortIcon(column.field)}
                                            </Box>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAndSortedAchats.map((achat) => (
                                    <TableRow 
                                        key={achat.id}
                                        hover
                                        sx={{ '&:hover': { backgroundColor: 'action.hover' }, height: 32 }}
                                    >
                                        {columns.map((column) => (
                                            <TableCell key={column.field}>
                                                {column.renderCell
                                                    ? (column.field === 'actions'
                                                        ? column.renderCell({ row: achat })
                                                        : column.renderCell({ value: achat[column.field] })
                                                    )
                                                    : (column.field === 'dateAchat'
                                                        ? (() => {
                                                            const date = new Date(achat[column.field]);
                                                            return isNaN(date) ? '' : date.toLocaleDateString('fr-FR');
                                                        })()
                                                        : achat[column.field]
                                                    )
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            
            {/* Modal pour afficher le PDF */}
            <Dialog
                open={openPdfDialog}
                onClose={() => setOpenPdfDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ p: 0, height: '80vh' }}>
                    {pdfUrl && (
                        <iframe
                            src={pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            title="Étiquette Amazon"
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog de confirmation pour tout expédier */}
            <Dialog
                open={confirmAllExpedieOpen}
                onClose={() => setConfirmAllExpedieOpen(false)}
            >
                <DialogTitle>Confirmer l'expédition</DialogTitle>
                <DialogContent>
                    Voulez-vous vraiment marquer <b>tous</b> les achats comme expédiés ?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAllExpedieOpen(false)}>Annuler</Button>
                    <Button onClick={handleMarkAllAsExpedie} color="success" autoFocus>
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Dialog de confirmation pour expédier un achat */}
            <Dialog
                open={!!confirmExpedieId}
                onClose={() => setConfirmExpedieId(null)}
            >
                <DialogTitle>Confirmer l'expédition</DialogTitle>
                <DialogContent>
                    Voulez-vous vraiment marquer cet achat comme expédié ?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmExpedieId(null)}>Annuler</Button>
                    <Button onClick={confirmUpdateStatut} color="success" autoFocus>
                        Confirmer
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AchatsEnAttenteExpedition; 